import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { EstimateMetrics } from '@paragon/contract-library';
import { projectArchived, projectSynchronized } from '@paragon/layout-models';

interface ProjectMetricsEntry {
  readonly status: 'idle' | 'loading' | 'loaded' | 'error';
  readonly requestId: string | null;
  readonly data?: EstimateMetrics;
}

interface ProjectMetricsState {
  readonly [projectGuid: string]: ProjectMetricsEntry | undefined;
}

interface MetricsRequest {
  readonly projectGuid: string;
  readonly requestId: string;
}

const initialState: ProjectMetricsState = {};

const projectMetricsSlice = createSlice({
  name: 'projectMetrics',
  initialState,
  reducers: {
    metricsRequested: (state, { payload }: PayloadAction<MetricsRequest>) => {
      state[payload.projectGuid] = { status: 'loading', requestId: payload.requestId };
    },

    metricsLoaded: (state, { payload }: PayloadAction<MetricsRequest & { readonly data: EstimateMetrics }>) => {
      if (state[payload.projectGuid]?.requestId !== payload.requestId) return;
      state[payload.projectGuid] = { status: 'loaded', requestId: payload.requestId, data: castDraft(payload.data) };
    },

    metricsFailed: (state, { payload }: PayloadAction<MetricsRequest>) => {
      if (state[payload.projectGuid]?.requestId !== payload.requestId) return;
      state[payload.projectGuid] = { status: 'error', requestId: payload.requestId };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(projectSynchronized, (state, action) => {
      for (const projectGuid of Object.keys(action.payload)) {
        if (state[projectGuid]) state[projectGuid] = { status: 'idle', requestId: null };
      }
    });

    builder.addCase(projectArchived, (state, action) => {
      delete state[action.payload.guid];
    });
  },
});

export const { metricsRequested, metricsLoaded, metricsFailed } = projectMetricsSlice.actions;
export const projectMetrics = projectMetricsSlice.reducer;
